import { cn } from "@d12labs/utils";
import type { CSSProperties } from "react";
import { type ListColumn, listGridStyle, listRowBase } from "./data-list";

/**
 * Loading placeholder block. A pulsing surface-2 rectangle; size it with `w`/`h`
 * or with utility classes via `className`.
 */
export function Skeleton({
	w = "100%",
	h = 12,
	className,
}: {
	w?: number | string;
	h?: number | string;
	className?: string;
}) {
	return (
		<span
			data-slot="skeleton"
			className={cn("inline-block animate-pulse rounded bg-surface-2", className)}
			style={{ width: w, height: h }}
		/>
	);
}

/**
 * A skeleton shaped like a dense list row — drop several in place of `ListRow`s
 * while a list query is loading so the layout doesn't jump on load.
 */
export function SkeletonRow({
	columns,
	className,
}: {
	/** Same column defs you pass to ListHeader/ListRow, for matching tracks. */
	columns?: ListColumn[];
	className?: string;
}) {
	const style: CSSProperties = columns
		? listGridStyle(columns)
		: { gridTemplateColumns: "20px 1.4fr 1fr 90px 60px" };
	const widths = ["12px", "60%", "50%", "70px", "40px"];
	const count = columns?.length ?? widths.length;
	return (
		<div className={cn(listRowBase, "h-11", className)} style={style}>
			{Array.from({ length: count }).map((_, i) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: fixed-length static placeholder
				<Skeleton key={i} w={widths[i] ?? "50%"} h={10} />
			))}
		</div>
	);
}
